"use client";

import * as React from "react";
import { addDays, format, isSameDay } from "date-fns";
import { srLatn } from "date-fns/locale";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { availabilityOptionVariants } from "./availability-option";

export function MobileDateStrip({
  selectedDate,
  onSelect,
  dailySummary,
  startDate,
}: {
  selectedDate: Date | undefined;
  onSelect: (date: Date) => void;
  dailySummary: Record<string, boolean> | undefined;
  startDate: string;
}) {
  const selectedRef = React.useRef<HTMLButtonElement>(null);

  const dates = React.useMemo(() => {
    const start = new Date(startDate);
    return Array.from({ length: 28 }, (_, i) => addDays(start, i));
  }, [startDate]);

  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [selectedDate]);

  return (
    <div
      className="flex gap-1.5 overflow-x-auto pb-1"
      style={{ scrollbarWidth: "none" }}
    >
      {dates.map((date) => {
        const dateKey = format(date, "yyyy-MM-dd");
        const isAvailable = dailySummary?.[dateKey] ?? false;
        const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
        const isCurrentDay = isSameDay(date, new Date());

        return (
          <button
            key={dateKey}
            ref={isSelected ? selectedRef : undefined}
            onClick={() => isAvailable && onSelect(date)}
            disabled={!isAvailable}
            className={availabilityOptionVariants({
              state: isSelected
                ? "selected"
                : isAvailable
                  ? "available"
                  : "unavailable",
              className:
                "flex shrink-0 flex-col items-center rounded-xl px-3 py-2",
            })}
          >
            <span className="text-[10px] font-medium uppercase">
              {format(date, "EEE", { locale: srLatn })}
            </span>
            <span className="text-lg font-semibold leading-tight">
              {format(date, "d")}
            </span>
            {isCurrentDay && (
              <span
                className={cn(
                  "mt-0.5 size-1 rounded-full",
                  isSelected ? "bg-brand-foreground" : "bg-brand-action",
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function MobileDateStripSkeleton() {
  return (
    <div className="flex gap-1.5 overflow-hidden">
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          className="flex shrink-0 flex-col items-center rounded-xl border border-border bg-secondary px-3 py-2"
        >
          <Skeleton className="h-3 w-6" />
          <Skeleton className="mt-1 h-5 w-4" />
        </div>
      ))}
    </div>
  );
}
